import { useState, useEffect, useCallback } from 'react';
import { BookUser } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { getBexioContacts } from '../lib/api';

export function BexioContactPicker({ onPick, disabled }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 280);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBexioContacts({ q: debounced, limit: 60 });
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 503) {
        toast.error(typeof detail === 'string' ? detail : 'Bexio is not configured on the server.');
      } else if (status === 429) {
        toast.error('bexio rate limit — try again in a moment.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Could not load bexio contacts.');
      }
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [debounced]);

  useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  const handlePick = (contact) => {
    onPick(contact);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0"
          disabled={disabled}
          title="Pick client from bexio"
          data-testid="bexio-contact-picker-trigger"
        >
          <BookUser className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(100vw-2rem,22rem)] p-0" align="end">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search bexio contacts…"
            value={search}
            onValueChange={setSearch}
            data-testid="bexio-contact-search"
          />
          <CommandList>
            <CommandEmpty>
              {loading ? 'Loading…' : 'No contacts found.'}
            </CommandEmpty>
            <CommandGroup>
              {items.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`${c.id}-${c.label}`}
                  onSelect={() => handlePick(c)}
                  className="cursor-pointer"
                  data-testid={`bexio-contact-${c.id}`}
                >
                  <span className={cn('truncate', c.nr != null && 'pr-2')}>{c.label}</span>
                  {c.nr != null && (
                    <span className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums">
                      #{c.nr}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
